import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import CashRunwayChart from "../../components/director/CashRunwayChart";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import ScopeNotice from "../../components/director/ScopeNotice";
import { useDirectorContext } from "@contexts/director";
import {
  balanceBlockerMessage,
  compactCents,
  formatCivilDate,
  formatCompetenceShort,
  formatFreshness,
} from "../../utils/directorFormat";
import {
  companyQuery,
  currentCompetence,
  oldestExtractedAt,
  scenarioLabel,
  shiftCompetence,
} from "./module1Utils";

const SCENARIOS = ["base", "conservative", "stress"];

const joinNames = (names) => {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} e ${names.at(-1)}`;
};

const earliest = (values) => values.filter(Boolean).sort()[0] || null;

const civilDate = (value) => (
  typeof value === "string" ? formatCivilDate(value.slice(0, 10)) : "data não informada"
);

const BlockedBalance = ({ blockers = [], canReviewGovernance, selectedCompanyId }) => <section
  className="director-state director-capital-blocked"
  role="status"
  aria-label="Saldo projetado bloqueado"
>
  <strong>O saldo projetado ainda não pode ser mostrado</strong>
  <p>O saldo projetado só aparece quando todos os caixas têm saldo inicial informado e as fontes fecham. Hoje falta:</p>
  <ul>
    {(blockers.length ? blockers : ["unknown_blocker"]).map((code) => <li key={code}>{balanceBlockerMessage(code)}</li>)}
  </ul>
  {canReviewGovernance && <Link className="director-button" to={`/dashboard/director/governance${companyQuery(selectedCompanyId)}`}>
    Revisar saldos e colchão
  </Link>}
</section>;

const Capital = () => {
  const { selectedCompanyId, accessByCompany = {} } = useDirectorContext();
  const [scenario, setScenario] = useState("base");
  const query = useMemo(() => {
    const from = currentCompetence();
    return { from, to: shiftCompetence(from, 11), scenario };
  }, [scenario]);
  const surface = useDirectorSurface("balance-projection", query);
  const { data } = surface;
  const capabilities = accessByCompany[selectedCompanyId]?.capabilities || [];
  const poolingGroups = data?.poolingGroups || [];
  const labelFor = (poolingGroupId) => (
    poolingGroups.find((pool) => pool.poolingGroupId === poolingGroupId)?.label || poolingGroupId
  );
  const poolLabels = Object.fromEntries(poolingGroups.map((pool) => [pool.poolingGroupId, pool.label || pool.poolingGroupId]));
  const risk = data?.risk || {};
  const byPool = risk.byPoolingGroup || [];
  const riskFor = (poolingGroupId) => byPool.find((item) => item.poolingGroupId === poolingGroupId) || {};
  const openingFor = (poolingGroupId) => (
    (data?.openingBalance?.pools || []).find((item) => item.poolingGroupId === poolingGroupId) || {}
  );
  const minimumPool = [...byPool]
    .filter((item) => Number.isSafeInteger(item.minimumBalanceCents))
    .sort((left, right) => left.minimumBalanceCents - right.minimumBalanceCents)[0];
  const minimumMonth = minimumPool
    ? poolingGroups.find((pool) => pool.poolingGroupId === minimumPool.poolingGroupId)?.minimumBalanceMonth
    : null;
  const firstNegative = earliest(byPool.map((item) => item.firstNegativeMonth)) || risk.firstNegativeMonth || null;
  const firstBelowBuffer = earliest(byPool.map((item) => item.firstBelowBufferMonth)) || risk.firstBelowBufferMonth || null;
  const criticalMonth = firstNegative || firstBelowBuffer;
  const criticalPool = byPool.find((item) => (
    firstNegative ? item.firstNegativeMonth === firstNegative : item.firstBelowBufferMonth === firstBelowBuffer
  ));
  const needing = byPool.filter((item) => item.fundingGapCents > 0).map((item) => labelFor(item.poolingGroupId));
  const surplus = poolingGroups
    .filter((pool) => !(riskFor(pool.poolingGroupId).fundingGapCents > 0))
    .map((pool) => pool.label || pool.poolingGroupId);
  const openingAsOf = earliest((data?.openingBalance?.pools || []).map((item) => item.asOf));
  const partialPools = poolingGroups.filter((pool) => pool.complete === false).map((pool) => pool.poolingGroupId);
  const freshness = oldestExtractedAt(data?.freshness);
  const available = data?.balanceAvailable === true;

  return <ModuleShell
    title="Capital necessário"
    description={available && openingAsOf
      ? `Quanto cada caixa vai precisar e quando. Saldos iniciais informados em ${civilDate(openingAsOf)}.`
      : "Quanto cada caixa vai precisar e quando, a partir do saldo inicial e do fluxo projetado."}
    badge={`${scenarioLabel(scenario)} · próximos 12 meses`}
    qualitySources={[surface]}
    showProjectFilter={false}
  >
    <section className="director-module1__filters">
      <label className="director-filter-field">
        <span>Cenário</span>
        <select value={scenario} onChange={(event) => setScenario(event.target.value)}>
          {SCENARIOS.map((value) => <option key={value} value={value}>{scenarioLabel(value)}</option>)}
        </select>
      </label>
    </section>
    <ModuleState
      {...surface}
      availability={data?.status === "no_data" || data?.status === "empty" ? "empty" : undefined}
      onRetry={surface.reload}
    />
    {data && !available && data.status !== "no_data" && <BlockedBalance
      blockers={data.blockers || []}
      canReviewGovernance={capabilities.includes("director.governance.read")}
      selectedCompanyId={selectedCompanyId}
    />}
    {data && available && <>
      <ScopeNotice
        completePortfolioScope={partialPools.length ? false : data.scope?.completePortfolioScope}
        partialPoolingGroupIds={partialPools}
      />
      <section className="director-module1__metrics director-module1__metrics--four" aria-label="Indicadores de capital">
        <MetricCard
          label="Menor saldo projetado"
          value={compactCents(risk.minimumBalanceCents)}
          note={minimumPool
            ? `${labelFor(minimumPool.poolingGroupId)}${minimumMonth ? `, em ${formatCompetenceShort(minimumMonth)}` : ""}`
            : "Sem caixa com saldo projetado"}
          tone={risk.minimumBalanceCents < 0 ? "pressure" : "neutral"}
        />
        <MetricCard
          label="Primeiro mês crítico"
          value={criticalMonth ? formatCompetenceShort(criticalMonth) : "Nenhum"}
          note={criticalMonth
            ? `${criticalPool ? `${labelFor(criticalPool.poolingGroupId)} fica` : "Um caixa fica"} ${firstNegative ? "abaixo de zero" : "abaixo do colchão"}`
            : "Nenhum caixa fica abaixo do colchão"}
          tone={criticalMonth ? "orange" : "green"}
        />
        <MetricCard
          label="Aporte para manter o colchão"
          value={compactCents(risk.fundingGapCents)}
          note={needing.length ? needing.join(" + ") : "Nenhum caixa precisa de aporte"}
          tone={risk.fundingGapCents > 0 ? "pressure" : "green"}
        />
        <MetricCard
          label="Aporte para não ficar negativo"
          value={compactCents(risk.gapToZeroCents)}
          note="Sem colchão"
        />
      </section>
      <CashRunwayChart
        months={data.months || []}
        showTable={false}
        poolLabels={poolLabels}
        eyebrow="Caixa a caixa"
        title="Saldo projetado por caixa"
        description="Próximos 12 meses. A linha tracejada de cada caixa é o colchão; a preta é o zero."
      />
      <section className="director-module1__split">
        <article className="director-module1__panel">
          <header>
            <div>
              <span className="director-eyebrow">Caixa por caixa</span>
              <h2>Quanto cada caixa precisa</h2>
            </div>
          </header>
          <div className="director-table-scroll">
            <table className="director-table director-owner-table" aria-label="Caixa por caixa">
              <thead>
                <tr>
                  <th scope="col">Caixa</th>
                  <th scope="col">Saldo hoje</th>
                  <th scope="col">Menor saldo</th>
                  <th scope="col">Quando</th>
                  <th scope="col">Fica negativo em</th>
                  <th scope="col">Aporte necessário</th>
                </tr>
              </thead>
              <tbody>
                {poolingGroups.map((pool) => {
                  const poolRisk = riskFor(pool.poolingGroupId);
                  return <tr key={pool.poolingGroupId} data-uncovered={poolRisk.fundingGapCents > 0 ? "true" : "false"}>
                    <th scope="row">{pool.label || pool.poolingGroupId}</th>
                    <td>{compactCents(openingFor(pool.poolingGroupId).balanceCents)}</td>
                    <td>{compactCents(poolRisk.minimumBalanceCents)}</td>
                    <td>{pool.minimumBalanceMonth ? formatCompetenceShort(pool.minimumBalanceMonth) : "—"}</td>
                    <td>{poolRisk.firstNegativeMonth ? formatCompetenceShort(poolRisk.firstNegativeMonth) : "Não fica"}</td>
                    <td>{compactCents(poolRisk.fundingGapCents)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </article>
        <aside className="director-module1__insight">
          <span className="director-eyebrow">Como ler</span>
          <h2>Sobra numa SPE não cobre a outra</h2>
          <p>{needing.length
            ? `${surplus.length ? `${joinNames(surplus)} não ${surplus.length === 1 ? "precisa" : "precisam"} de aporte no período. ` : ""}Para cobrir ${joinNames(needing)} com esse dinheiro é preciso uma operação entre as empresas, como mútuo, distribuição ou novo aporte.`
            : "Nenhum caixa precisa de aporte no período. Mesmo assim, a sobra de uma SPE só chega à outra por uma operação entre as empresas."}</p>
          <div className="director-module1__callout">O saldo projetado só aparece quando todos os caixas têm saldo inicial informado.</div>
        </aside>
      </section>
    </>}
    {data && freshness && <footer className="director-freshness">Fontes atualizadas em {formatFreshness(freshness)}.</footer>}
  </ModuleShell>;
};

export default Capital;
